import { Autocomplete, Chip, Stack, TextField } from "@mui/material";
import { useGetQualificationsForWorkplaceType } from "@src/appV2/Admin/WorkplaceTypes/api/useGetQualificationsForWorkplaceType";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useRef, useState } from "react";

import { Dialog } from "../../components/Dialog";
import { DialogFooter } from "../../components/DialogFooter";
import { Text } from "../../components/Text";
import { TextInput } from "../../components/TextInput";
import { type PreShiftOrientationFormValues } from "../hooks/useUpdatePreShiftOrientationSetting";
import { buildPreShiftOrientationSummary } from "../utils/buildPreShiftOrientationSummary";
import { SettingEditableContent } from "./SettingEditableContent";

const ORIENTATION_LENGTH_MINIMUM_MINUTES = 1;
const ORIENTATION_LENGTH_MAXIMUM_MINUTES = 120;
const ORIENTATION_LENGTH_DEFAULT_MINUTES = 15;
const EMPTY_QUALIFICATIONS: string[] = [];

interface QualificationOption {
  value: string;
  label: string;
}

interface PreShiftOrientationSettingProps {
  initialOrientationLengthInMinutes: number | undefined;
  initialQualifications: string[] | undefined;
  isEnabled: boolean;
  isSaving: boolean;
  modalState: UseModalState;
  workplaceId: string;
  workplaceTypeName: string | undefined;
  onSave: (values: PreShiftOrientationFormValues) => void;
}

export function PreShiftOrientationSetting({
  initialOrientationLengthInMinutes,
  initialQualifications,
  isEnabled,
  isSaving,
  modalState,
  workplaceId,
  workplaceTypeName,
  onSave,
}: PreShiftOrientationSettingProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const orientationLengthInMinutes =
    initialOrientationLengthInMinutes ?? ORIENTATION_LENGTH_DEFAULT_MINUTES;
  const initialQualificationsList = initialQualifications ?? EMPTY_QUALIFICATIONS;
  const { qualifications: workplaceQualifications } = useGetQualificationsForWorkplaceType({
    workplaceTypeName,
    workplaceId,
  });

  const qualificationOptions: QualificationOption[] = workplaceQualifications.map(
    (qualification) => ({
      value: qualification.attributes.name,
      label: qualification.attributes.displayLabel,
    })
  );

  const summary = buildPreShiftOrientationSummary({
    orientationLengthInMinutes,
    qualifications: initialQualificationsList,
    qualificationOptions,
  });

  const [lengthInput, setLengthInput] = useState(String(orientationLengthInMinutes));
  const [qualifications, setQualifications] = useState<string[]>(initialQualificationsList);

  const wasModalOpenRef = useRef(modalState.modalIsOpen);
  useEffect(() => {
    if (modalState.modalIsOpen && !wasModalOpenRef.current) {
      setLengthInput(String(orientationLengthInMinutes));
      setQualifications(initialQualificationsList);
    }
    wasModalOpenRef.current = modalState.modalIsOpen;
  }, [modalState.modalIsOpen, orientationLengthInMinutes, initialQualificationsList]);

  const parsedLength = Number(lengthInput);
  const isLengthInvalid =
    lengthInput === "" ||
    !Number.isInteger(parsedLength) ||
    parsedLength < ORIENTATION_LENGTH_MINIMUM_MINUTES ||
    parsedLength > ORIENTATION_LENGTH_MAXIMUM_MINUTES;
  const lengthHelperText = isLengthInvalid
    ? tu("Orientation length must be a whole number between {{minimum}} and {{maximum}} minutes", {
        values: {
          maximum: ORIENTATION_LENGTH_MAXIMUM_MINUTES,
          minimum: ORIENTATION_LENGTH_MINIMUM_MINUTES,
        },
      })
    : undefined;

  const qualificationsValueSet = new Set(qualifications);
  const selectedQualificationOptions = qualificationOptions.filter((option) =>
    qualificationsValueSet.has(option.value)
  );

  return (
    <>
      {isEnabled && (
        <SettingEditableContent
          buttonLabel={tu("Edit Start of Shift Orientation")}
          disabled={isSaving}
          subtitle={summary}
          title={tu("Start of Shift Orientation configuration")}
          onEdit={modalState.openModal}
        />
      )}
      <Dialog
        modalState={modalState}
        title={tu("Edit Start of Shift Orientation")}
        width={500}
        footerContent={
          <DialogFooter
            primaryAction={{
              label: tu("Save"),
              disabled: isLengthInvalid || isSaving,
              isLoading: isSaving,
              onClick: () => {
                onSave({
                  orientationLengthInMinutes: parsedLength,
                  qualifications,
                });
              },
            }}
            secondaryAction={{
              label: tu("Cancel"),
              onClick: modalState.closeModal,
            }}
          />
        }
      >
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Text semibold variant="body2">
              {tu("Orientation length")}
            </Text>
            <Stack direction="row" alignItems="center" spacing={2}>
              <TextInput
                type="number"
                value={lengthInput}
                disabled={isSaving}
                error={isLengthInvalid}
                inputProps={{
                  min: ORIENTATION_LENGTH_MINIMUM_MINUTES,
                  max: ORIENTATION_LENGTH_MAXIMUM_MINUTES,
                  step: 1,
                  "aria-label": tu("Orientation length"),
                }}
                sx={{ width: 120 }}
                onChange={(event) => {
                  setLengthInput(event.target.value);
                }}
              />
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {tu("minutes")}
              </Text>
            </Stack>
            {isLengthInvalid && (
              <Text variant="caption" sx={(theme) => ({ color: theme.palette.error.main })}>
                {lengthHelperText}
              </Text>
            )}
          </Stack>

          <Stack spacing={1}>
            <Text semibold variant="body2">
              {tu("Qualification-specific orientation")}
            </Text>
            <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {tu(
                "Leave empty to apply orientation at the workplace level: only a single orientation is required for a professional for any qualification. Select qualifications to require orientation only for those, with each completed orientation counting toward the qualification it covered."
              )}
            </Text>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={isSaving}
              options={qualificationOptions}
              value={selectedQualificationOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, selected) => option.value === selected.value}
              size="small"
              renderTags={(tagValues, getTagProps) =>
                tagValues.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={qualifications.length === 0 ? tu("Any qualification") : undefined}
                />
              )}
              onChange={(_event, newSelected) => {
                setQualifications(newSelected.map((option) => option.value));
              }}
            />
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}
